import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GridProps, GridViewType } from '../../../types/datagrid';
import { GridToolbar } from './GridToolbar';
import { GridHeader } from './GridHeader';
import { GridBody } from './GridBody';
import { GridPagination } from './GridPagination';
import { ViewManager } from './ViewManager';
import { ColumnManager } from './ColumnManager';
import { useDataGrid } from './hooks/useDataGrid';
import { useGridSort } from './hooks/useGridSort';
import { useGridFilter } from './hooks/useGridFilter';
import { useGridPagination } from './hooks/useGridPagination';
import { useGridColumns } from './hooks/useGridColumns';
import { useGridViews } from './hooks/useGridViews';

export interface DataGridProps extends GridProps {
  gridId: string; // Unique identifier for this grid instance
}

export const DataGrid: React.FC<DataGridProps> = ({
  gridId,
  data,
  columns: initialColumns,
  loading = false,
  error,
  defaultView,
  availableViews = [],
  toolbarFilters = [],
  actions = [],
  rowActions = [],
  enableSorting = true,
  enableColumnReorder = true,
  enableColumnResize = true,
  enableInlineEdit = false,
  enableRowSelection = false,
  enableVirtualization = true,
  pagination,
  onPaginationChange,
  onSort,
  onFilter,
  onColumnChange,
  onViewChange,
  onViewSave,
  onRowSelect,
  onCellEdit,
  onRowClick,
  onRowDoubleClick,
  onRowAction,
  className = '',
  rowHeight = 44,
  headerHeight = 48,
  stickyHeader = true,
  stickyToolbar = true,
  compactDensity = true,
  permissions = {},
}) => {
  // Refs
  const gridRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  // Local state
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState(false);
  const [isViewManagerOpen, setIsViewManagerOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [viewType, setViewType] = useState<GridViewType>('list');

  // Custom hooks
  const { state, dispatch } = useDataGrid({
    gridId,
    data,
    columns: initialColumns,
    defaultView,
    loading,
    error,
    pagination: pagination || { page: 1, limit: 10, total: 0, totalPages: 0 },
  });

  const { sorting, handleSort, handleMultiSort } = useGridSort({
    initialSorting: state.sorting,
    onSort,
  });

  const { filters, handleFilterChange, resetFilters } = useGridFilter({
    initialFilters: state.filters,
    onFilter,
  });

  const { paginationState, handlePageChange, handleLimitChange } = useGridPagination({
    initialPagination: state.pagination,
    onPaginationChange,
  });

  const {
    columns,
    visibleColumns,
    handleColumnReorder,
    handleColumnResize,
    handleColumnVisibilityChange,
    handleColumnRename,
    resetColumns,
  } = useGridColumns({
    initialColumns: state.columns,
    onColumnChange,
  });

  const {
    currentView,
    views,
    handleViewChange,
    handleViewSave: saveView,
    handleViewDelete,
    handleViewReset,
  } = useGridViews({
    gridId,
    initialViews: availableViews,
    defaultView: state.currentView,
    onViewChange,
    onViewSave,
  });

  // Memoized computed values
  const filteredData = useMemo(() => {
    if (!Object.keys(filters).length) return data;

    return data.filter(row => {
      return Object.entries(filters).every(([key, value]) => {
        if (!value || value === '') return true;

        const cellValue = row[key];
        if (cellValue == null) return false;

        // Handle different filter types
        if (Array.isArray(value)) {
          return value.includes(cellValue);
        }

        if (typeof value === 'string') {
          return cellValue.toString().toLowerCase().includes(value.toLowerCase());
        }

        return cellValue === value;
      });
    });
  }, [data, filters]);

  const sortedData = useMemo(() => {
    if (!sorting.length) return filteredData;

    return [...filteredData].sort((a, b) => {
      for (const sort of sorting) {
        const aVal = a[sort.field];
        const bVal = b[sort.field];

        if (aVal === bVal) continue;

        let comparison = 0;

        if (aVal == null) comparison = -1;
        else if (bVal == null) comparison = 1;
        else if (typeof aVal === 'string') {
          comparison = aVal.localeCompare(bVal);
        } else if (typeof aVal === 'number') {
          comparison = aVal - bVal;
        } else if (aVal instanceof Date) {
          comparison = aVal.getTime() - bVal.getTime();
        }

        if (comparison !== 0) {
          return sort.direction === 'desc' ? -comparison : comparison;
        }
      }
      return 0;
    });
  }, [filteredData, sorting]);

  const paginatedData = useMemo(() => {
    const start = (paginationState.page - 1) * paginationState.limit;
    const end = start + paginationState.limit;
    return sortedData.slice(start, end);
  }, [sortedData, paginationState.page, paginationState.limit]);

  // Event handlers
  const handleRowSelection = useCallback(
    (rowIds: string[]) => {
      setSelectedRows(rowIds);
      onRowSelect?.(rowIds);
    },
    [onRowSelect]
  );

  const handleCellEditSave = useCallback(
    async (rowId: string, columnId: string, value: any) => {
      try {
        await onCellEdit?.(rowId, columnId, value);
        dispatch({ type: 'EDIT_CELL_SUCCESS' });
      } catch (error) {
        dispatch({
          type: 'EDIT_CELL_ERROR',
          payload: error instanceof Error ? error.message : 'Save failed',
        });
      }
    },
    [onCellEdit, dispatch]
  );

  const handleViewTypeChange = useCallback((newViewType: GridViewType) => {
    setViewType(newViewType);
  }, []);

  const handleExport = useCallback(
    (format: 'csv' | 'xlsx' | 'pdf') => {
      // Export functionality will be implemented in a separate utility
      console.log('Export to', format, 'with data:', paginatedData);
    },
    [paginatedData]
  );

  // Update state when props change
  useEffect(() => {
    dispatch({
      type: 'UPDATE_DATA',
      payload: {
        data,
        loading,
        error,
        pagination: pagination || state.pagination,
      },
    });
  }, [data, loading, error, pagination, dispatch, state.pagination]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE_COLUMNS',
      payload: columns,
    });
  }, [columns, dispatch]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE_SORTING',
      payload: sorting,
    });
  }, [sorting, dispatch]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE_FILTERS',
      payload: filters,
    });
  }, [filters, dispatch]);

  // Keyboard navigation handler
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const { key, ctrlKey, metaKey } = event;
      const isMod = ctrlKey || metaKey;

      switch (key) {
        case 'Escape':
          setSelectedRows([]);
          dispatch({ type: 'CANCEL_EDIT' });
          break;
        case 'Enter':
          if (state.editingCell) {
            event.preventDefault();
            // Save current edit
          }
          break;
        case 'Tab':
          if (state.editingCell) {
            event.preventDefault();
            // Move to next editable cell
          }
          break;
        case 'a':
          if (isMod && enableRowSelection) {
            event.preventDefault();
            const allRowIds = paginatedData.map(row => row.id || row._id);
            handleRowSelection(allRowIds);
          }
          break;
      }
    },
    [state.editingCell, enableRowSelection, paginatedData, handleRowSelection, dispatch]
  );

  // Calculate total height for virtualization
  // const totalHeight = useMemo(() => {
  //   return (
  //     (stickyToolbar ? 56 : 0) + // Toolbar height
  //     headerHeight +
  //     paginatedData.length * rowHeight +
  //     (pagination ? 56 : 0)
  //   ); // Pagination height
  // }, [stickyToolbar, headerHeight, paginatedData.length, rowHeight, pagination]);

  if (viewType !== 'list') {
    return (
      <div className="flex flex-col h-full">
        <GridToolbar
          filters={toolbarFilters}
          filterValues={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={resetFilters}
          actions={actions}
          selectedRows={selectedRows}
          data={paginatedData}
          viewType={viewType}
          onViewTypeChange={handleViewTypeChange}
          onColumnManager={() => setIsColumnManagerOpen(true)}
          onViewManager={() => setIsViewManagerOpen(true)}
          onExport={handleExport}
          sticky={stickyToolbar}
          permissions={permissions}
        />

        <div className="flex-1 flex items-center justify-center text-gray-500">
          <div className="text-center">
            <p className="text-lg font-medium">View type "{viewType}" coming soon</p>
            <p className="text-sm">Switch to List view to use the data grid</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={gridRef}
      className={`bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden ${className}`}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="grid"
      aria-label="Data Grid"
      aria-rowcount={sortedData.length}
      aria-colcount={visibleColumns.length}
    >
      {/* Toolbar */}
      <GridToolbar
        filters={toolbarFilters}
        filterValues={filters}
        onFilterChange={handleFilterChange}
        onResetFilters={resetFilters}
        actions={actions}
        selectedRows={selectedRows}
        data={paginatedData}
        viewType={viewType}
        onViewTypeChange={handleViewTypeChange}
        onColumnManager={() => setIsColumnManagerOpen(true)}
        onViewManager={() => setIsViewManagerOpen(true)}
        onExport={handleExport}
        sticky={stickyToolbar}
        permissions={permissions}
      />

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-50 border-b border-red-200">
          <div className="flex items-center space-x-2 text-red-700">
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-sm font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* Grid Content */}
      <div className="relative flex-1 overflow-auto">
        {/* Header */}
        <GridHeader
          ref={headerRef}
          columns={visibleColumns}
          sorting={sorting}
          onSort={enableSorting ? handleSort : undefined}
          onMultiSort={enableSorting ? handleMultiSort : undefined}
          onColumnResize={enableColumnResize ? handleColumnResize : undefined}
          onColumnReorder={enableColumnReorder ? handleColumnReorder : undefined}
          height={headerHeight}
          sticky={stickyHeader}
          compactDensity={compactDensity}
          permissions={{ canEdit: permissions?.canBulkEdit }}
        />

        {/* Body */}
        <GridBody
          data={paginatedData}
          columns={visibleColumns}
          loading={loading}
          selectedRows={selectedRows}
          onRowSelect={enableRowSelection ? handleRowSelection : undefined}
          onRowClick={onRowClick}
          onRowDoubleClick={onRowDoubleClick}
          onCellEdit={enableInlineEdit ? handleCellEditSave : undefined}
          editingCell={state.editingCell}
          rowHeight={rowHeight}
          enableVirtualization={enableVirtualization && paginatedData.length > 50}
          compactDensity={compactDensity}
          permissions={{ canEdit: permissions?.canBulkEdit, canDelete: permissions?.canDelete }}
          rowActions={rowActions}
          onRowAction={onRowAction}
        />
      </div>

      {/* Pagination */}
      {pagination && (
        <GridPagination
          pagination={{
            ...paginationState,
            total: sortedData.length,
            totalPages: Math.ceil(sortedData.length / paginationState.limit),
          }}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          loading={loading}
        />
      )}

      {/* Column Manager Modal */}
      {isColumnManagerOpen &&
        createPortal(
          <ColumnManager
            isOpen={isColumnManagerOpen}
            onClose={() => setIsColumnManagerOpen(false)}
            columns={columns}
            onColumnChange={newColumns => {
              onColumnChange?.(newColumns);
            }}
            onColumnReorder={handleColumnReorder}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            onColumnRename={handleColumnRename}
            onReset={resetColumns}
            permissions={permissions}
          />,
          document.body
        )}

      {/* View Manager Modal */}
      {isViewManagerOpen &&
        createPortal(
          <ViewManager
            isOpen={isViewManagerOpen}
            onClose={() => setIsViewManagerOpen(false)}
            currentView={currentView}
            views={views}
            onViewChange={handleViewChange}
            onViewSave={saveView}
            onViewDelete={handleViewDelete}
            onViewReset={handleViewReset}
            gridState={{
              columns,
              sorting,
              filters,
              pagination: paginationState,
            }}
            permissions={permissions}
          />,
          document.body
        )}
    </div>
  );
};
